interface AdminHierarchyNode<TNode> {
  id: string | number
  parentId: string | number
  menuName: string
  menuCode?: string | null
  sortOrder?: number
  children: TNode[]
}

const COUNTRY_SITE_ROOT_NAMES = new Set(['国家站点管理', '国家站点管理后台'])
const LEGACY_GAME_SPECIAL_NAME = '游戏专题'
const LEGACY_GAME_SPECIAL_CODE = 'business:content:game-special'
const REMOVED_BONUS_THEME_NAME = '礼金主题'
const REMOVED_BONUS_THEME_CODES = new Set(['site:theme:query', 'site:theme:create', 'site:theme:update', 'site:theme:delete'])
const REMOVED_CAROUSEL_NAME = '轮播管理'
const REMOVED_CAROUSEL_CODES = new Set([
  'site:post-banner:query',
  'site:post-banner:create',
  'site:post-banner:update',
  'site:post-banner:delete'
])
const RETIRED_SITE_SLOT_MACHINE_CODES = new Set([
  'site:lucky-wheel-prize:query',
  'site:lucky-wheel-prize:create',
  'site:lucky-wheel-prize:update',
  'site:lucky-wheel-prize:delete'
])
const RETIRED_PROS_CONS_NAME = '优缺点管理'
const RETIRED_PROS_CONS_CODES = new Set([
  'center:pros-cons-tag:query',
  'center:pros-cons-tag:create',
  'center:pros-cons-tag:update',
  'center:pros-cons-tag:delete'
])
const REMOVED_ROUTE_MANAGEMENT_CODE = 'country-site:special:config'
const REMOVED_SITE_ARTICLE_ROUTE_CODE = 'country-site:route:articles'
const GAME_MANAGEMENT_NAME = '游戏管理'
const SITE_GAME_MENU_SORT_ORDERS = new Map<string, number>([
  ['site:demo-game:config', 10],
  ['site:game-classification:query', 20],
  ['country-site:provider:config', 30],
  ['site:demo-game:topic', 40]
])
const ACTIVITY_MANAGEMENT_NAME = '活动管理'
const USER_MANAGEMENT_NAME = '用户管理'
const DATA_CONFIG_NAME = '数据配置'
const SITE_PAGE_CENTER_CODE = 'country-site:page-center'
const SITE_PAGE_CENTER_SORT_ORDER = 10
const SITE_SOCIAL_MEDIA_CODE = 'site:social-media:query'
const SITE_SOCIAL_MEDIA_SORT_ORDER = 30
const SITE_LOGIN_PAGE_IMAGE_CODE = 'country-site:login-page-image'
const SITE_LOGIN_PAGE_IMAGE_SORT_ORDER = 40
const SITE_FAQ_CODE = 'site:faq:query'
const SITE_FAQ_SORT_ORDER = 50
const SITE_FOOTER_CONFIG_NAME = '页脚配置'
const SITE_FOOTER_CONFIG_SORT_ORDER = 60
const MULTIPLIER_CHALLENGE_CODE = 'business:activity:multiplier-challenge'
const MULTIPLIER_CHALLENGE_SORT_ORDER = 30
const SITE_AUTHOR_CODE = 'site:author:query'
const SITE_AUTHOR_SORT_ORDER = 20

function isLegacyGameSpecial(node: { menuName: string; menuCode?: string | null }) {
  return node.menuName === LEGACY_GAME_SPECIAL_NAME || node.menuCode === LEGACY_GAME_SPECIAL_CODE
}

function isRemovedBonusTheme(node: { menuName: string; menuCode?: string | null }) {
  return node.menuName === REMOVED_BONUS_THEME_NAME || Boolean(node.menuCode && REMOVED_BONUS_THEME_CODES.has(node.menuCode))
}

function isRetiredProsCons(node: { menuName: string; menuCode?: string | null }) {
  return node.menuName === RETIRED_PROS_CONS_NAME || Boolean(node.menuCode && RETIRED_PROS_CONS_CODES.has(node.menuCode))
}

function isRemovedCarousel(node: { menuName: string; menuCode?: string | null }) {
  return node.menuName === REMOVED_CAROUSEL_NAME || Boolean(node.menuCode && REMOVED_CAROUSEL_CODES.has(node.menuCode))
}

function isRemovedMenu(node: { menuName: string; menuCode?: string | null }) {
  return (
    isLegacyGameSpecial(node) ||
    isRemovedBonusTheme(node) ||
    isRemovedCarousel(node) ||
    isRetiredProsCons(node) ||
    Boolean(node.menuCode && RETIRED_SITE_SLOT_MACHINE_CODES.has(node.menuCode)) ||
    node.menuCode === REMOVED_ROUTE_MANAGEMENT_CODE ||
    node.menuCode === REMOVED_SITE_ARTICLE_ROUTE_CODE
  )
}

function detachHierarchyNode<T extends AdminHierarchyNode<T>>(nodes: T[], matcher: (node: T) => boolean): T | undefined {
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index]
    if (matcher(node)) return nodes.splice(index, 1)[0]
    const child = detachHierarchyNode(node.children, matcher)
    if (child) return child
  }
}

function sortHierarchy<T extends AdminHierarchyNode<T>>(nodes: T[]) {
  nodes.sort(
    (left, right) =>
      (left.sortOrder ?? Number.MAX_SAFE_INTEGER) - (right.sortOrder ?? Number.MAX_SAFE_INTEGER) ||
      String(left.id).localeCompare(String(right.id))
  )
  nodes.forEach(node => sortHierarchy(node.children))
}

/**
 * 保留后端返回的两棵顶级菜单树，过滤已废弃菜单，并兼容已调整归属的菜单父级。
 * 菜单 ID 和角色勾选状态保持后端真实值，只调整前端展示层级与路由位置。
 */
export function normalizeAdminMenuHierarchy<T extends AdminHierarchyNode<T>>(roots: T[]): T[] {
  const cloneNode = (node: T): T => {
    const children = node.children.filter(child => !isRemovedMenu(child)).map(cloneNode)
    return { ...node, children } as T
  }

  const normalizedRoots = roots.filter(root => !isRemovedMenu(root)).map(cloneNode)
  const countrySiteRoot = normalizedRoots.find(
    root => String(root.parentId) === '0' && COUNTRY_SITE_ROOT_NAMES.has(root.menuName)
  )
  if (countrySiteRoot) {
    const gameManagement = countrySiteRoot.children.find(node => node.menuName === GAME_MANAGEMENT_NAME && !node.menuCode)
    if (gameManagement) {
      SITE_GAME_MENU_SORT_ORDERS.forEach((sortOrder, menuCode) => {
        const gameMenu = detachHierarchyNode(normalizedRoots, node => node.menuCode === menuCode)
        if (!gameMenu) return
        gameManagement.children.push({ ...gameMenu, parentId: gameManagement.id, sortOrder } as T)
      })
    }

    const activityManagement = countrySiteRoot.children.find(node => node.menuName === ACTIVITY_MANAGEMENT_NAME)
    if (activityManagement) {
      const multiplierChallenge = detachHierarchyNode(normalizedRoots, node => node.menuCode === MULTIPLIER_CHALLENGE_CODE)
      if (multiplierChallenge) {
        activityManagement.children.push({
          ...multiplierChallenge,
          parentId: activityManagement.id,
          sortOrder: MULTIPLIER_CHALLENGE_SORT_ORDER
        } as T)
      }
    }

    const userManagement = countrySiteRoot.children.find(node => node.menuName === USER_MANAGEMENT_NAME)
    if (userManagement) {
      const authorManagement = detachHierarchyNode(normalizedRoots, node => node.menuCode === SITE_AUTHOR_CODE)
      if (authorManagement) {
        userManagement.children.push({
          ...authorManagement,
          parentId: userManagement.id,
          sortOrder: SITE_AUTHOR_SORT_ORDER
        } as T)
      }
    }

    const dataConfig = countrySiteRoot.children.find(node => node.menuName === DATA_CONFIG_NAME && !node.menuCode)
    if (dataConfig) {
      const pageConfig = detachHierarchyNode(normalizedRoots, node => node.menuCode === SITE_PAGE_CENTER_CODE)
      if (pageConfig) {
        dataConfig.children.push({
          ...pageConfig,
          parentId: dataConfig.id,
          sortOrder: SITE_PAGE_CENTER_SORT_ORDER
        } as T)
      }
      const socialMedia = detachHierarchyNode(normalizedRoots, node => node.menuCode === SITE_SOCIAL_MEDIA_CODE)
      if (socialMedia) {
        dataConfig.children.push({
          ...socialMedia,
          parentId: dataConfig.id,
          sortOrder: SITE_SOCIAL_MEDIA_SORT_ORDER
        } as T)
      }
      const loginPageImage = detachHierarchyNode(normalizedRoots, node => node.menuCode === SITE_LOGIN_PAGE_IMAGE_CODE)
      if (loginPageImage) {
        dataConfig.children.push({
          ...loginPageImage,
          parentId: dataConfig.id,
          sortOrder: SITE_LOGIN_PAGE_IMAGE_SORT_ORDER
        } as T)
      }
      const faq = detachHierarchyNode(normalizedRoots, node => node.menuCode === SITE_FAQ_CODE)
      if (faq) {
        dataConfig.children.push({ ...faq, parentId: dataConfig.id, sortOrder: SITE_FAQ_SORT_ORDER } as T)
      }
      const footerConfig = detachHierarchyNode(normalizedRoots, node => node.menuName === SITE_FOOTER_CONFIG_NAME)
      if (footerConfig) {
        dataConfig.children.push({
          ...footerConfig,
          parentId: dataConfig.id,
          sortOrder: SITE_FOOTER_CONFIG_SORT_ORDER
        } as T)
      }
    }
  }
  sortHierarchy(normalizedRoots)
  return normalizedRoots
}

interface AuthorizedHierarchyMenu {
  id: string
  parentId: string
  name: string
  menuCode?: string | null
  sortOrder: number
}

/** 对 `/auth/me` 的扁平菜单应用与菜单管理、角色权限树相同的废弃过滤和父级迁移。 */
export function normalizeAuthorizedMenuHierarchy<T extends AuthorizedHierarchyMenu>(menus: T[]): T[] {
  const removedIds = new Set(
    menus.filter(menu => isRemovedMenu({ menuName: menu.name, menuCode: menu.menuCode })).map(menu => String(menu.id))
  )
  let changed = true
  while (changed) {
    changed = false
    menus.forEach(menu => {
      if (!removedIds.has(String(menu.parentId)) || removedIds.has(String(menu.id))) return
      removedIds.add(String(menu.id))
      changed = true
    })
  }

  const filtered = menus.filter(menu => !removedIds.has(String(menu.id)))
  const countrySiteRoot = filtered.find(menu => String(menu.parentId) === '0' && COUNTRY_SITE_ROOT_NAMES.has(menu.name))
  if (!countrySiteRoot) return filtered

  const activityManagement = filtered.find(
    menu => String(menu.parentId) === String(countrySiteRoot.id) && menu.name === ACTIVITY_MANAGEMENT_NAME
  )
  const userManagement = filtered.find(
    menu => String(menu.parentId) === String(countrySiteRoot.id) && menu.name === USER_MANAGEMENT_NAME
  )
  const gameManagement = filtered.find(
    menu => String(menu.parentId) === String(countrySiteRoot.id) && menu.name === GAME_MANAGEMENT_NAME && !menu.menuCode
  )
  const dataConfig = filtered.find(
    menu => String(menu.parentId) === String(countrySiteRoot.id) && menu.name === DATA_CONFIG_NAME && !menu.menuCode
  )

  return filtered.map(menu => {
    const gameSortOrder = menu.menuCode ? SITE_GAME_MENU_SORT_ORDERS.get(menu.menuCode) : undefined
    if (gameManagement && gameSortOrder !== undefined) {
      return { ...menu, parentId: gameManagement.id, sortOrder: gameSortOrder } as T
    }
    if (activityManagement && menu.menuCode === MULTIPLIER_CHALLENGE_CODE) {
      return {
        ...menu,
        parentId: activityManagement.id,
        sortOrder: MULTIPLIER_CHALLENGE_SORT_ORDER
      } as T
    }
    if (userManagement && menu.menuCode === SITE_AUTHOR_CODE) {
      return { ...menu, parentId: userManagement.id, sortOrder: SITE_AUTHOR_SORT_ORDER } as T
    }
    if (dataConfig && menu.menuCode === SITE_PAGE_CENTER_CODE) {
      return {
        ...menu,
        parentId: dataConfig.id,
        sortOrder: SITE_PAGE_CENTER_SORT_ORDER
      } as T
    }
    if (dataConfig && menu.menuCode === SITE_SOCIAL_MEDIA_CODE) {
      return { ...menu, parentId: dataConfig.id, sortOrder: SITE_SOCIAL_MEDIA_SORT_ORDER } as T
    }
    if (dataConfig && menu.menuCode === SITE_LOGIN_PAGE_IMAGE_CODE) {
      return { ...menu, parentId: dataConfig.id, sortOrder: SITE_LOGIN_PAGE_IMAGE_SORT_ORDER } as T
    }
    if (dataConfig && menu.menuCode === SITE_FAQ_CODE) {
      return { ...menu, parentId: dataConfig.id, sortOrder: SITE_FAQ_SORT_ORDER } as T
    }
    if (dataConfig && menu.name === SITE_FOOTER_CONFIG_NAME) {
      return { ...menu, parentId: dataConfig.id, sortOrder: SITE_FOOTER_CONFIG_SORT_ORDER } as T
    }
    return menu
  })
}
